import { motion, useTransform } from "framer-motion";
import { C, Art, Browser, Owner, Designer, Plant, Rise, Pop, Slide, Draw, Mark, useP } from "@/components/illustrations/kit";

const scallops = (x, y, n, w) => {
  let d = `M${x} ${y}`;
  for (let i = 0; i < n; i++) d += ` a${w / 2} ${w / 2} 0 0 0 ${w} 0`;
  return `${d} V${y - 4} H${x}Z`;
};

const Dots = ({ x, y, cols = 6, rows = 4 }) => (
  <g fill={C.grey}>
    {Array.from({ length: cols * rows }).map((_, i) => (
      <circle key={i} cx={x + (i % cols) * 14} cy={y + Math.floor(i / cols) * 14} r="1.6" />
    ))}
  </g>
);

const useLayers = (lite) => {
  const p = useP();
  const back = useTransform(p, [0, 1], lite ? [0, 0] : [30, -30]);
  const front = useTransform(p, [0, 1], lite ? [0, 0] : [-10, 10]);
  return { back, front };
};

const useWalk = (from, a, b) => {
  const p = useP();
  const x = useTransform(p, [a, b], [from, 0], { clamp: true });
  const steps = [a, a + (b - a) * 0.2, a + (b - a) * 0.4, a + (b - a) * 0.6, a + (b - a) * 0.8, b];
  const leg = useTransform(p, steps, [10, -10, 10, -10, 10, 0], { clamp: true });
  const bob = useTransform(p, steps, [0, -4, 0, -4, 0, 0], { clamp: true });
  return { x, leg, bob };
};

export const SceneIdea = ({ lite }) => {
  const p = useP();
  const { back, front } = useLayers(lite);
  const walk = useWalk(-170, 0.06, 0.34);
  const armRot = useTransform(p, [0.34, 0.46], [160, 0], { clamp: true });
  const headTilt = useTransform(p, [0.34, 0.46], [0, -8], { clamp: true });
  const glow = useTransform(p, [0.42, 0.5, 0.58], [0.4, 1, 0.75], { clamp: true });
  return (
    <Art label="A shop owner walks to her storefront, raises her hand with an idea, and the idea becomes a small website">
      <motion.g style={{ y: back }}>
        <circle cx="440" cy="220" r="170" fill={C.mint} />
        <circle cx="110" cy="80" r="38" fill="none" stroke={C.line} strokeWidth="10" />
        <Dots x={520} y={40} />
      </motion.g>
      <rect x="0" y="420" width="640" height="60" fill="#EEF3F0" />
      <Rise d={-0.5}>
        <rect x="50" y="150" width="230" height="270" rx="4" fill={C.ink2} />
        <rect x="95" y="165" width="140" height="32" rx="4" fill={C.em} />
        <rect x="103" y="172" width="124" height="18" rx="2" fill="none" stroke={C.deep} strokeWidth="2" />
        <rect x="38" y="208" width="254" height="34" rx="4" fill={C.em} />
        <path d={scallops(38, 242, 10, 25.4)} fill={C.em} />
        <rect x="66" y="268" width="120" height="104" rx="3" fill="#EEF3F0" />
        <path d="M78 300 H174 M78 336 H174" stroke={C.grey} strokeWidth="3" />
        <Plant x={96} y={336} s={0.32} />
        <Plant x={150} y={300} s={0.28} />
        <rect x="200" y="268" width="62" height="152" rx="2" fill="#EEF3F0" stroke={C.ink2} strokeWidth="6" />
        <circle cx="250" cy="346" r="4" fill={C.ink2} />
        <Plant x={318} y={420} s={0.7} />
      </Rise>
      <motion.g style={{ y: front }}>
        <motion.g style={{ x: walk.x, y: walk.bob }}>
          <Owner x={385} y={420} s={0.92} pose="idea" armRot={armRot} headTilt={headTilt} legSwing={walk.leg} />
        </motion.g>
        <Pop d={0.5}><circle cx="436" cy="142" r="4" fill={C.em} /></Pop>
        <Pop d={0.58}><circle cx="450" cy="122" r="6" fill={C.em} /></Pop>
        <Pop d={0.68}>
          <circle cx="492" cy="88" r="36" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
          <motion.circle cx="492" cy="82" r="14" fill={C.dot} style={{ opacity: glow }} />
          <rect x="485" y="96" width="14" height="10" rx="3" fill={C.em} />
          <path d="M492 56 V50 M470 64 L466 60 M514 64 L518 60" stroke={C.em} strokeWidth="3" strokeLinecap="round" />
        </Pop>
        <Draw cue={0.8} d="M528 98 Q586 118 570 178" fill="none" stroke={C.em} strokeWidth="2.5" strokeDasharray="6 6" />
        <Rise d={0.95}>
          <Browser x={438} y={186} w={180} h={136} />
        </Rise>
        <g transform="translate(438 186)">
          <Rise d={1.08} dy={12}>
            <rect x="12" y="40" width="72" height="9" rx="4.5" fill={C.ink} />
            <rect x="12" y="55" width="52" height="9" rx="4.5" fill={C.em} />
            <rect x="12" y="72" width="64" height="5" rx="2.5" fill={C.grey} />
          </Rise>
          <Pop d={1.2}><rect x="12" y="96" width="50" height="18" rx="9" fill={C.em} /></Pop>
          <Slide d={1.15} x={24}>
            <rect x="96" y="38" width="72" height="80" rx="8" fill={C.mint} />
            <rect x="104" y="52" width="56" height="12" rx="3" fill={C.em} />
            <rect x="108" y="68" width="48" height="42" rx="2" fill={C.ink2} />
            <rect x="114" y="74" width="20" height="36" fill="#EEF3F0" />
          </Slide>
        </g>
      </motion.g>
    </Art>
  );
};

export const SceneBuild = ({ lite }) => {
  const p = useP();
  const { back, front } = useLayers(lite);
  const t = [0.12, 0.2, 0.28, 0.36, 0.44, 0.52, 0.6, 0.68, 0.76];
  const bob = useTransform(p, t, [0, 3, 0, 3, 0, 3, 0, 3, 0], { clamp: true });
  const armL = useTransform(p, t, [0, -4, 0, -4, 0, -4, 0, -4, 0], { clamp: true });
  const armR = useTransform(p, t, [-4, 0, -4, 0, -4, 0, -4, 0, -4], { clamp: true });
  const press = useTransform(p, [0.66, 0.7, 0.74], [1, 0.88, 1], { clamp: true });
  return (
    <Art label="A designer types on a laptop while a website interface assembles itself piece by piece">
      <motion.g style={{ y: back }}>
        <circle cx="170" cy="300" r="150" fill={C.mint} />
        <circle cx="590" cy="420" r="28" fill="none" stroke={C.line} strokeWidth="9" />
        <Dots x={30} y={40} cols={5} rows={4} />
      </motion.g>
      <Rise d={-0.5}>
        <rect x="20" y="360" width="300" height="12" rx="4" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
        <rect x="40" y="372" width="10" height="80" fill={C.grey} />
        <rect x="290" y="372" width="10" height="80" fill={C.grey} />
        <Designer x={170} y={360} bob={bob} armL={armL} armR={armR} />
        <Plant x={56} y={358} s={0.5} />
        <rect x="262" y="336" width="22" height="24" rx="4" fill="#FFFFFF" stroke={C.grey} strokeWidth="2" />
        <path d="M284 342 q10 2 0 12" stroke={C.grey} strokeWidth="3" fill="none" />
      </Rise>
      <Draw cue={0} d="M232 300 Q270 250 300 230" fill="none" stroke={C.em} strokeWidth="2.5" strokeDasharray="6 6" />
      <motion.g style={{ y: front }}>
        <Rise d={-0.05}>
          <Browser x={300} y={50} w={320} h={340} />
        </Rise>
        <g transform="translate(300 50)">
          <Slide d={0.15} y={-16}>
            <rect x="20" y="42" width="16" height="16" rx="4" fill={C.em} />
            <rect x="160" y="47" width="28" height="6" rx="3" fill={C.grey} />
            <rect x="196" y="47" width="28" height="6" rx="3" fill={C.grey} />
            <rect x="232" y="47" width="28" height="6" rx="3" fill={C.grey} />
            <rect x="270" y="42" width="34" height="16" rx="8" fill={C.ink} />
          </Slide>
          <Slide d={0.3} x={-30}>
            <rect x="20" y="82" width="168" height="15" rx="4" fill={C.ink} />
            <rect x="20" y="104" width="120" height="15" rx="4" fill={C.ink} />
          </Slide>
          <Rise d={0.4} dy={10}>
            <rect x="20" y="130" width="150" height="6" rx="3" fill={C.grey} />
            <rect x="20" y="142" width="120" height="6" rx="3" fill={C.grey} />
          </Rise>
          <Pop d={0.5}>
            <motion.rect x="20" y="160" width="76" height="24" rx="12" fill={C.em} style={{ scale: press, transformBox: "fill-box", transformOrigin: "center" }} />
          </Pop>
          <Slide d={0.42} x={40}>
            <rect x="206" y="78" width="96" height="106" rx="12" fill={C.mint} />
            <Plant x={254} y={172} s={0.6} />
          </Slide>
          {[0, 1, 2].map((i) => (
            <Rise key={i} d={0.6 + i * 0.1} dy={34}>
              <rect x={20 + i * 96} y="206" width="88" height="112" rx="12" fill={C.soft} stroke={C.line} strokeWidth="2" />
              <circle cx={40 + i * 96} cy="230" r="10" fill={i === 1 ? C.em : C.leaf} />
              <rect x={32 + i * 96} y="254" width="60" height="6" rx="3" fill={C.ink} opacity="0.8" />
              <rect x={32 + i * 96} y="268" width="48" height="5" rx="2.5" fill={C.grey} />
              <rect x={32 + i * 96} y="280" width="54" height="5" rx="2.5" fill={C.grey} />
            </Rise>
          ))}
          <Slide d={0.95} x={190} y={130} len={0.2}>
            <path d="M70 172 l0 26 l7 -6 l5 11 l5 -2 l-5 -11 l9 -1z" fill={C.ink} stroke="#FFFFFF" strokeWidth="2" strokeLinejoin="round" />
          </Slide>
        </g>
      </motion.g>
    </Art>
  );
};

const SiteLayout = ({ w, cols }) => {
  const cw = (w - 16 - (cols - 1) * 6) / cols;
  return (
    <g>
      <rect x="8" y="8" width="10" height="10" rx="3" fill={C.em} />
      <rect x={w - 30} y="10" width="22" height="6" rx="3" fill={C.ink} />
      <rect x="8" y="28" width={w - 16} height={cols === 1 ? 58 : 70} rx="6" fill={C.mint} />
      <rect x="16" y="40" width={Math.min(120, w * 0.45)} height="9" rx="3" fill={C.ink} />
      <rect x="16" y="54" width={Math.min(80, w * 0.32)} height="9" rx="3" fill={C.em} />
      <rect x="16" y={cols === 1 ? 70 : 76} width="34" height="10" rx="5" fill={C.em} />
      {Array.from({ length: cols }).map((_, i) => (
        <g key={i}>
          <rect x={8 + i * (cw + 6)} y={cols === 1 ? 94 : 108} width={cw} height={cols === 1 ? 30 : 48} rx="5" fill={C.soft} stroke={C.line} />
          <circle cx={16 + i * (cw + 6)} cy={cols === 1 ? 104 : 120} r="4" fill={C.leaf} />
        </g>
      ))}
    </g>
  );
};

const Tick = ({ x, y, d }) => (
  <Pop d={d}>
    <circle cx={x} cy={y} r="13" fill={C.em} stroke="#FFFFFF" strokeWidth="3" />
    <path d={`M${x - 5} ${y} l3.5 3.5 l6.5 -7`} stroke="#FFFFFF" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Pop>
);

const Label = ({ x, y, t, d }) => (
  <Pop d={d}>
    <rect x={x} y={y} width={t.length * 7.6 + 20} height="22" rx="11" fill="#FFFFFF" stroke={C.line} />
    <text x={x + 10} y={y + 15} fontFamily="IBM Plex Mono, monospace" fontSize="10" fill={C.muted} letterSpacing="1.2">{t}</text>
  </Pop>
);

export const SceneDevices = ({ lite }) => {
  const { back, front } = useLayers(lite);
  return (
    <Art label="The same website adapts across a desktop monitor, a tablet and a phone">
      <motion.g style={{ y: back }}>
        <circle cx="330" cy="230" r="190" fill={C.mint} />
        <Dots x={540} y={30} cols={5} rows={3} />
      </motion.g>
      <rect x="20" y="400" width="600" height="10" rx="5" fill={C.line} />
      <Rise d={-0.4}>
        <rect x="40" y="62" width="380" height="244" rx="14" fill={C.ink} />
        <rect x="52" y="74" width="356" height="220" rx="6" fill="#FFFFFF" />
        <rect x="208" y="306" width="44" height="72" fill={C.grey} />
        <rect x="168" y="378" width="124" height="22" rx="6" fill={C.grey} />
      </Rise>
      <Rise d={0.05} dy={16}>
        <g transform="translate(52 74) scale(1.6)">
          <SiteLayout w={222} cols={3} />
        </g>
      </Rise>
      <motion.g style={{ y: front }}>
        <Slide d={0.3} x={180} len={0.18}>
          <rect x="372" y="176" width="156" height="224" rx="16" fill={C.ink} />
          <rect x="381" y="186" width="138" height="204" rx="8" fill="#FFFFFF" />
          <g transform="translate(381 192) scale(1.04)">
            <SiteLayout w={132} cols={2} />
          </g>
        </Slide>
        <Slide d={0.55} x={160} len={0.18}>
          <rect x="526" y="248" width="88" height="152" rx="16" fill={C.ink} />
          <rect x="532" y="256" width="76" height="136" rx="10" fill="#FFFFFF" />
          <g transform="translate(532 262) scale(0.96)">
            <SiteLayout w={80} cols={1} />
          </g>
        </Slide>
        <Tick x={414} y={66} d={0.85} />
        <Tick x={522} y={180} d={0.95} />
        <Tick x={608} y={252} d={1.05} />
        <Label x={48} y={22} t="DESKTOP" d={0.9} />
        <Label x={404} y={140} t="TABLET" d={1.0} />
        <Label x={540} y={420} t="MOBILE" d={1.1} />
      </motion.g>
    </Art>
  );
};

export const SceneLaunch = ({ lite }) => {
  const { back, front } = useLayers(lite);
  const walk = useWalk(-150, 0.1, 0.4);
  return (
    <Art label="The finished website goes online and the confident business owner stands beside it">
      <motion.g style={{ y: back }}>
        <Slide d={0.05} x={40} y={30}>
          <rect x="236" y="74" width="384" height="312" rx="24" fill={C.em} />
        </Slide>
        <circle cx="80" cy="90" r="34" fill="none" stroke={C.line} strokeWidth="10" />
      </motion.g>
      <rect x="0" y="430" width="640" height="50" fill="#EEF3F0" />
      <motion.g style={{ y: front }}>
        <Rise d={-0.3}>
          <Browser x={212} y={52} w={384} h={312}>
            <Mark x={20} y={38} s={18} />
            <text x="44" y="51" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="800" fontSize="10" letterSpacing="1.4" fill={C.ink}>YOUR BRAND</text>
            <rect x="244" y="43" width="26" height="6" rx="3" fill={C.grey} />
            <rect x="278" y="43" width="26" height="6" rx="3" fill={C.grey} />
            <rect x="312" y="38" width="52" height="16" rx="8" fill={C.ink} />
          </Browser>
        </Rise>
        <g transform="translate(212 52)">
          <Slide d={0.2} x={-24}>
            <text x="20" y="110" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="800" fontSize="30" fill={C.ink}>Your story,</text>
          </Slide>
          <Slide d={0.3} x={-24}>
            <text x="20" y="144" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="800" fontSize="30" fill={C.ink}>
              now <tspan fill={C.em}>online.</tspan>
            </text>
          </Slide>
          <Rise d={0.38} dy={10}>
            <rect x="20" y="160" width="150" height="6" rx="3" fill={C.grey} />
            <rect x="20" y="172" width="118" height="6" rx="3" fill={C.grey} />
          </Rise>
          <Pop d={0.48}>
            <rect x="20" y="192" width="92" height="28" rx="14" fill={C.em} />
            <text x="34" y="210" fontFamily="General Sans, sans-serif" fontWeight="600" fontSize="10" fill="#FFFFFF">Get started</text>
          </Pop>
          <Slide d={0.35} x={36}>
            <rect x="214" y="72" width="150" height="150" rx="14" fill={C.mint} />
            <rect x="226" y="92" width="126" height="16" rx="4" fill={C.em} />
            <path d={scallops(226, 116, 6, 21)} fill={C.em} />
            <rect x="234" y="124" width="110" height="98" fill={C.ink2} />
            <rect x="244" y="134" width="54" height="50" fill="#EEF3F0" />
            <rect x="306" y="134" width="30" height="88" fill="#EEF3F0" />
          </Slide>
          {[0, 1, 2].map((i) => (
            <Rise key={i} d={0.58 + i * 0.1} dy={24}>
              <rect x={20 + i * 116} y="240" width="108" height="52" rx="10" fill={C.soft} stroke={C.line} />
              <circle cx={38 + i * 116} cy="266" r="8" fill={i === 0 ? C.em : i === 1 ? C.ink : C.leaf} />
              <rect x={54 + i * 116} y="260" width="56" height="5" rx="2.5" fill={C.ink} opacity="0.75" />
              <rect x={54 + i * 116} y="270" width="40" height="5" rx="2.5" fill={C.grey} />
            </Rise>
          ))}
        </g>
        <Pop d={0.9}>
          <rect x="190" y="30" width="96" height="30" rx="15" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
          <circle cx="208" cy="45" r="5" fill={C.em} className="animate-pulseDot" />
          <text x="220" y="49" fontFamily="IBM Plex Mono, monospace" fontSize="11" letterSpacing="1.2" fill={C.ink}>ONLINE</text>
        </Pop>
        <Pop d={1.0}>
          <circle cx="604" cy="104" r="22" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
          <path d="M604 93 a8 8 0 0 1 8 8 c0 6 -8 14 -8 14 s-8 -8 -8 -14 a8 8 0 0 1 8 -8z" fill="none" stroke={C.em} strokeWidth="2.5" />
          <circle cx="604" cy="101" r="2.6" fill={C.em} />
        </Pop>
        <Pop d={1.1}>
          <circle cx="610" cy="330" r="22" fill="#FFFFFF" stroke={C.line} strokeWidth="2" />
          <path d="M610 318 l3.6 7.4 8 1.2 -5.8 5.6 1.4 8 -7.2 -3.8 -7.2 3.8 1.4 -8 -5.8 -5.6 8 -1.2z" fill={C.em} />
        </Pop>
        <motion.g style={{ x: walk.x, y: walk.bob }}>
          <Owner x={118} y={432} s={1.02} pose="confident" legSwing={walk.leg} />
        </motion.g>
      </motion.g>
    </Art>
  );
};

export const SCENE_ART = [SceneIdea, SceneBuild, SceneDevices, SceneLaunch];
